import { must } from "@/common/utils/must";
import StyleFlags from "@/modules/StyleFlags";
import * as orientationManager from "@/modules/orientationManager";
import { orientationType } from "@/modules/orientationManager";
import type ExperienceMetadata from "@/modules/ExperienceMetadata";
import ExperienceVideo from "@/modules/ExperienceVideo";
import type {
  AssetImage,
  ExperienceArticle,
  ExperienceId,
  ExperiencePayload,
} from "@/types/experience";

/** A `{ type, src }` source for Video.js, or "" when there is no video. */
export type { VideoSource } from "@/modules/ExperienceVideo";

export {
  articleTypes,
  clusterTypes,
  experienceTypes,
  integrationTypes,
  statusTypes,
  whiskTypes,
} from "@/modules/experienceConstants";

export type MerchantPayloadEntry = NonNullable<
  ExperiencePayload["merchants"]
>[number];

export default class ExperienceData extends ExperienceVideo {
  // Payload fields copied by Object.assign; the index signature keeps the rest open.
  [key: string]: unknown;
  declare idexperience: ExperiencePayload["idexperience"];
  declare name: ExperiencePayload["name"];
  declare title: ExperiencePayload["title"];
  declare description: ExperiencePayload["description"];
  declare status: ExperiencePayload["status"];
  declare style: ExperiencePayload["style"];
  declare logo: ExperiencePayload["logo"];
  declare poster: ExperiencePayload["poster"];
  declare banner: ExperiencePayload["banner"];
  declare recipeMetadata: ExperiencePayload["recipeMetadata"];
  declare features: ExperiencePayload["features"];
  declare askEnabled: ExperiencePayload["askEnabled"];
  /** Playback position in seconds, set by the experience store's timeSync action. */
  declare nowTime: number | undefined;

  declare clusterNames: string[];
  declare merchantsById: Record<string, MerchantPayloadEntry>;
  declare styleFlags: StyleFlags;
  declare videoMarkersReached: { id: unknown }[];
  declare styleId: number | undefined;

  constructor(
    metadata: ExperienceMetadata,
    experienceData?: ExperiencePayload | null,
  ) {
    super();
    Object.assign(this, experienceData);
    this.metadata = metadata;
    this.articlesById = this._createArticlesById();
    this.clusterMap = this._createClusterMap();
    this.clusterNames = Object.keys(this.clusterMap);
    this.integrationsById = this._createIntegrationsById();
    this.merchantsById = this._createMerchantsById();
    this.styleFlags = new StyleFlags(this.style ? this.style.flags : undefined);
    this.videoMarkersReached = [];
    this.styleId = experienceData ? experienceData.idstyle : 1;

    this._checkClusters();
    this._checkIntegrations();
  }

  hasArticlesWithCategoryTag(categoryName: string): boolean {
    const ids = this._getArticleIdsByCategoryTag(categoryName);
    return ids && ids.length > 0;
  }

  isLoaded() {
    return !!(this.idexperience && this.video && this.video.modes);
  }

  getIntegration() {
    return this.idintegration
      ? this.integrationsById[this.idintegration] || null
      : null;
  }

  getIntegrationType() {
    const integration = this.getIntegration();
    return integration ? integration.type : null;
  }

  getMerchant(idmerchant: ExperienceId) {
    return this.merchantsById[idmerchant];
  }

  getStatus(): string {
    if (this.status) {
      return this.status;
    }
    return "draft";
  }

  /*
   * Gets platform specific article images.
   * returns array of images for a specific article.
   */
  getImagesFromArticle(
    article: ExperienceArticle | null,
  ): AssetImage[] | null | undefined {
    let ret: AssetImage[] | null | undefined = null;
    if (article === null || !article.images) {
      return ret;
    }

    if (
      orientationManager.getOrientation() === orientationType.PORTRAIT &&
      Object.hasOwn(article.images, "mobile")
    ) {
      ret = article.images.mobile;
    } else if (Object.hasOwn(article.images, "desktop")) {
      ret = article.images.desktop;
    }

    if (ret == null && Object.hasOwn(article.images, "all")) {
      ret = article.images.all;
    }

    return ret;
  }

  getArticleImages(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): AssetImage[] | null | undefined {
    if (!this.idexperience) {
      return null;
    }
    return this.getImagesFromArticle(this.getArticle(name, cindex, index));
  }

  getArticleImagesById(id: ExperienceId): AssetImage[] | null | undefined {
    if (!this.idexperience) {
      return null;
    }
    return this.getImagesFromArticle(this.getArticleById(id));
  }

  /*
   * Returns the image object for the article at index.
   */
  getArticleImage(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): AssetImage | null {
    if (!this.idexperience) {
      return null;
    }
    const images = this.getArticleImages(name, cindex, index);

    if (images === null) {
      return null;
    }
    const list = must(images, "article images");
    return list.length === 0 ? null : list[0];
  }

  getArticleImageById(id: ExperienceId): AssetImage | null {
    if (!this.idexperience) {
      return null;
    }
    const images = this.getArticleImagesById(id);

    if (images === null) {
      return null;
    }
    const list = must(images, "article images");
    return list.length === 0 ? null : list[0];
  }

  getArticleImageUrl(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): string | null {
    const image = this.getArticleImage(name, cindex, index);
    let url;

    if (image === null) {
      url = null;
    } else {
      url = this.metadata.getAssetUrl(image.filename);
    }

    return url;
  }

  getArticleImageUrlById(id: ExperienceId): string | null {
    const image = this.getArticleImageById(id);
    return image === null ? null : this.metadata.getAssetUrl(image.filename);
  }

  getLogoImageUrl(): string | null {
    if (!this.idexperience) {
      return "";
    }

    // CDP logo-observability preview override. `?logo_override=<full
    // url>` swaps in a specific logo for the CDP's preview embeds. Honored ONLY
    // under the `/preview/` path (noindex, robots-disallowed; the CDP embeds
    // /preview/logo-experience) — so a canonical, indexable, shareable /{id}
    // page can NEVER be spoofed with this param, even though every render path
    // calls this shared method. The pathname gate is the enforcement: the
    // canonical route is always /{id}, never /preview/*.
    if (
      typeof window !== "undefined" &&
      window.location &&
      typeof window.location.pathname === "string" &&
      window.location.pathname.indexOf("/preview/") === 0
    ) {
      try {
        const override = new URLSearchParams(window.location.search).get(
          "logo_override",
        );
        if (override) {
          return override;
        }
      } catch {
        /* no URLSearchParams / bad query — fall through to the real logo */
      }
    }

    // Render the same stored logo file in both orientations — no
    // orientation-specific asset. Prefer the single `all` file the publisher
    // API serves; fall back to desktop/mobile only if `all` is absent.
    let filename = null;
    if (this.logo) {
      if (Object.hasOwn(this.logo, "all")) {
        filename = must(this.logo.all, "logo.all").filename;
      } else if (Object.hasOwn(this.logo, "desktop")) {
        filename = must(this.logo.desktop, "logo.desktop").filename;
      } else if (Object.hasOwn(this.logo, "mobile")) {
        filename = must(this.logo.mobile, "logo.mobile").filename;
      }
    }

    if (filename) {
      return this.metadata.getAssetUrl(filename);
    }
    return null;
  }

  getPosterImageUrl(): string {
    if (!this.idexperience) {
      return "";
    }
    let poster =
      this.poster && this.poster.filename ? this.poster.filename : null;
    if (!poster) {
      poster =
        this.banner && this.banner.filename ? this.banner.filename : null;
    }
    if (!poster) {
      poster = "blank-experience-poster.png";
    }
    return this.metadata.getAssetUrl(poster);
  }

  getBannerImageUrl(): string {
    if (!this.idexperience) {
      return "";
    }
    let banner =
      this.banner && this.banner.filename ? this.banner.filename : null;
    if (!banner) {
      banner =
        this.poster && this.poster.filename ? this.poster.filename : null;
    }
    if (!banner) {
      banner = "blank-experience-banner.png";
    }
    return this.metadata.getAssetUrl(banner);
  }

  getMerchantLogoUrl(idmerchant: ExperienceId): string | undefined {
    const merchant = this.getMerchant(idmerchant);
    if (merchant && merchant.logo) {
      return this.metadata.getMerchantFileUrl(merchant.logo.filename);
    }
  }

  videoMarkerReached(marker: { id: unknown }) {
    for (let n = 0, max = this.videoMarkersReached.length; n < max; n++) {
      if (this.videoMarkersReached[n].id === marker.id) {
        return;
      }
    }

    this.videoMarkersReached.push(marker);
  }

  getVideoMarkerReachedCount() {
    return this.videoMarkersReached.length;
  }

  cacheArticleImages() {
    if (!this.articles) {
      return;
    }
    for (let i = 0; i < this.articles.length; i++) {
      this._cacheArticleImage(this.articles[i].id);
    }
  }

  hasRecipeMetadata(): boolean {
    if (this.description) {
      return true;
    } else if (this.recipeMetadata) {
      const m = this.recipeMetadata;
      return !!(
        this.description ||
        m.prepTime ||
        m.cookTime ||
        m.servings ||
        m.totalTime ||
        m.calories
      );
    }
    return false;
  }

  _cacheArticleImage(id: ExperienceId) {
    const articleImage = this.getArticleImageById(id);
    if (!articleImage) {
      return;
    }
    const img = new Image();
    img.src = this.metadata.getAssetUrl(articleImage.filename);
  }
}
